import React, {Suspense} from 'react';
import {ROUTE_PATHS} from '@di-shared/routes';
import {useNavigate} from 'react-router';
import useSestSearchParams from '@di-shared/features/diag/exchange/useSestSearchParams';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import CarInfo, {CarInfoLoading} from '@di-shared/features/diag/exchange/CarInfo';
import {useSearchParams} from 'react-router-dom';
import SestExchangeSelectGroup, {SestExchangeSelectGroupLoading} from '@di-shared/features/diag/exchange/select/SestExchangeSelectGroup';
import {emitEventLog} from '@di-shared/features/analytics';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import Header from '@di-shared/features/common/Header';
import HeaderCloseButton from '@di-shared/features/common/HeaderCloseButton';
import ScrollToTopOnMount from '@di-shared/features/common/ScrollToTopOnMount';

interface SestExchangeSelectPageProps {
  vin: string;
}

function SestExchangeSelectPage({vin}: SestExchangeSelectPageProps) {
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {getSestCodesFromParams, isCheckedSest, getToggledSestCodes} = useSestSearchParams({searchParams});
  const paramSestCodes = getSestCodesFromParams();
  const isAccessDI = !!searchParams.get('isAccessDI');

  function gotoRegisterPage() {
    navigate({
      pathname: ROUTE_PATHS.DIAG_EXCHANGE_REGISTER.getFullPath(vin),
      search: new URLSearchParams(searchParams).toString(),
    });
  }

  return (
    <div className="wrap">
      <ScrollToTopOnMount />
      <Header right={<HeaderCloseButton onClick={() => ccWebviewUtils.closeContent()} />}>
        <h1 className="headline_xxsmall nav_center_title">{t('T30027')}</h1>
      </Header>
      <main className="content page_car_management">
        <section className="section section_exchange_register">
          <div className="section_heading type_main">
            <h2 className="section_heading_title">{t('T30150')}</h2>
          </div>
          <Suspense fallback={<CarInfoLoading />}>
            <CarInfo vin={vin} />
          </Suspense>

          <ul className="section_bullet_list body_small_r">
            <li
              className="section_bullet_item"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('T30153')}
            </li>
          </ul>
          <Suspense fallback={<SestExchangeSelectGroupLoading />}>
            <SestExchangeSelectGroup
              toggleSestCode={(sestCode) =>
                setSearchParams(
                  {
                    sestCode: getToggledSestCodes(sestCode),
                    ...(isAccessDI ? {isAccessDI: '1'} : {}),
                  },
                  {replace: true}
                )
              }
              isCheckedSest={isCheckedSest}
              vin={vin}
            />
          </Suspense>
        </section>
      </main>

      <div className="floating_action_area">
        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          onClick={() => {
            emitEventLog({
              event: 'consumables_exchange_update',
              form_step: 'select consumable item',
              click_text: t('T30154'),
              click_location: 'body',
            });
            gotoRegisterPage();
          }}
          disabled={paramSestCodes.length < 1}
        >
          {t('T30154')}
        </button>
      </div>
    </div>
  );
}

export default SestExchangeSelectPage;
